import { useEffect, useRef, useState } from "react";
import { Download, Mic, RotateCcw, Square } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { PROMPTER_TEXT } from "@/components/story/content";

export default function VoiceRecorder({ onStart, onStop, title }) {
  const { lang } = useT();
  const tx = PROMPTER_TEXT[lang];
  const recRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [url, setUrl] = useState(null);
  const [secs, setSecs] = useState(0);

  useEffect(() => () => { recRef.current?.stream.getTracks().forEach((t) => t.stop()); }, []);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  useEffect(() => {
    if (!recording) return undefined;
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [recording]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setUrl(URL.createObjectURL(new Blob(chunks, { type: rec.mimeType || "audio/webm" })));
      };
      recRef.current = rec;
      rec.start();
      setUrl(null);
      setSecs(0);
      setRecording(true);
      onStart();
    } catch {
      toast.error(tx.micErr);
    }
  };

  const stop = () => { recRef.current?.stop(); setRecording(false); onStop(); };
  const ext = recRef.current?.mimeType?.includes("mp4") ? "m4a" : "webm";
  const fileName = `${(title || "mi-guion").replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "-") || "mi-guion"}.${ext}`;

  return (
    <div className="bg-white rounded-[2rem] shadow-lg p-5 flex flex-wrap items-center gap-4" data-testid="voice-recorder">
      {recording ? (
        <button type="button" onClick={stop} className="glossy-btn bg-slate-900 text-white font-fredoka font-bold px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="rec-stop-btn">
          <Square className="w-5 h-5 fill-white" /> {tx.stop}
        </button>
      ) : (
        <button type="button" onClick={start} className="glossy-btn bg-rose-500 text-white font-fredoka font-bold px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="rec-start-btn">
          {url ? <RotateCcw className="w-5 h-5" /> : <Mic className="w-5 h-5" />} {url ? tx.again : tx.rec}
        </button>
      )}
      {recording && (
        <span className="inline-flex items-center gap-2 font-bold text-rose-600" data-testid="rec-status">
          <span className="w-3 h-3 rounded-full bg-rose-500 animate-pulse" /> {tx.recording} {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}
        </span>
      )}
      {url && !recording && (
        <div className="flex flex-wrap items-center gap-3" data-testid="rec-result">
          <span className="font-bold text-blue-900">{tx.listen}</span>
          <audio src={url} controls data-testid="rec-audio" />
          <a href={url} download={fileName} className="inline-flex items-center gap-2 font-bold text-teal-700 underline" data-testid="rec-download-link">
            <Download className="w-4 h-4" /> {tx.download}
          </a>
        </div>
      )}
    </div>
  );
}
